import { Heading, Stack, Text } from '../design/index.js';
import { OrbitBackdrop } from './OrbitBackdrop.js';
import styles from './SignIn.module.css';

/**
 * Sign-in is a plain link, not a fetch.
 *
 * The browser must follow the redirect to Google itself so the OAuth state
 * cookie is set on a real navigation; an XHR would be blocked by Google's
 * cross-origin rules and would lose the cookie round-trip.
 */
export interface SignInProps {
  error?: string | null;
  /** Overrides the default subtitle, e.g. when arriving with an invitation
   *  pending — see `App.tsx`. Optional so every existing call site, which
   *  passes only `error`, keeps working unchanged. */
  reason?: string | null;
}

export function SignIn({ error, reason }: SignInProps) {
  return (
    <main className={styles.page}>
      <section className={styles.scene}>
        <OrbitBackdrop />
        <div className={styles.pitch}>
          <Stack gap={8}>
            <Text as="div" className={styles.wordmark}>log.gy</Text>
            <Heading level={1} className={styles.headline}>
              Everything you decided to keep track of, in one orbit.
            </Heading>
            <Text color="secondary" className={styles.sub}>
              Weight and heart rate from your Withings devices, the goals you set
              yourself, and the family you came from.
            </Text>
          </Stack>
        </div>
      </section>

      <section className={styles.panel}>
        <Stack gap={8} className={styles.form}>
          <Stack gap={2}>
            <Heading level={2}>Welcome back</Heading>
            <Text color="muted">{reason ?? 'Sign in to continue to your dashboard.'}</Text>
          </Stack>

          {error ? (
            <div role="alert" className={styles.error}>
              <Text size="sm">{error}</Text>
            </div>
          ) : null}

          <a className={styles.google} href="/api/auth/google/start">
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.0 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
              <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.0 6.1 29.3 4 24 4c-7.7 0-14.4 4.4-17.7 10.7z"/>
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.2 5.2C37.0 40.2 44 35 44 24c0-1.3-.1-2.3-.4-3.5z"/>
            </svg>
            Continue with Google
          </a>

          <Text size="xs" color="muted">
            Been invited to someone&rsquo;s dashboard? Open the link in your
            invitation instead.
          </Text>
        </Stack>
      </section>
    </main>
  );
}
